import { supabase } from '../lib/supabase';

class DealerService {
  // ==========================================
  // DEALERS
  // ==========================================

  async getAllDealers() {
    try {
      const { data, error } = await supabase
        .from('dealers')
        .select('*')
        .order('company_name');

      if (error) throw error;
      return { data, error: null };
    } catch (err) {
      console.error('DealerService getAllDealers Error:', err);
      return { data: [], error: err };
    }
  }

  async getDealerById(id) {
    try {
      const { data, error } = await supabase
        .from('dealers')
        .select(`
          *,
          dealer_cities(
            *,
            city:cities(*)
          )
        `)
        .eq('id', id)
        .single();

      if (error) throw error;
      return { data, error: null };
    } catch (err) {
      console.error('DealerService getDealerById Error:', err);
      return { data: null, error: err };
    }
  }

  async getDealerBySlug(slug) {
    try {
      const { data, error } = await supabase
        .from('dealers')
        .select(`
          *,
          dealer_cities(
            *,
            city:cities(*)
          )
        `)
        .eq('slug', slug)
        .single();

      if (error) throw error;
      return { data, error: null };
    } catch (err) {
      console.error('DealerService getDealerBySlug Error:', err);
      return { data: null, error: err };
    }
  }

  async createDealer(dealerData) {
    try {
      // Generate slug from company name
      const slug = dealerData.slug || 
        dealerData.company_name
          .toLowerCase()
          .replace(/[^a-z0-9]+/g, '-')
          .replace(/(^-|-$)/g, '');

      const { data, error } = await supabase
        .from('dealers')
        .insert([{ ...dealerData, slug }])
        .select()
        .single();

      if (error) throw error;
      return { data, error: null };
    } catch (err) {
      console.error('DealerService createDealer Error:', err);
      return { data: null, error: err };
    }
  }

  async updateDealer(id, updates) {
    try {
      const { data, error } = await supabase
        .from('dealers')
        .update(updates)
        .eq('id', id)
        .select()
        .single();

      if (error) throw error;
      return { data, error: null };
    } catch (err) {
      console.error('DealerService updateDealer Error:', err);
      return { data: null, error: err };
    }
  }

  async deleteDealer(id) {
    try {
      const { error } = await supabase
        .from('dealers')
        .delete()
        .eq('id', id);

      if (error) throw error;
      return { error: null };
    } catch (err) {
      console.error('DealerService deleteDealer Error:', err);
      return { error: err };
    }
  }

  async toggleDealerStatus(id, isActive) {
    return this.updateDealer(id, { is_active: isActive });
  }

  async updateSubscription(id, status, plan, endDate) {
    return this.updateDealer(id, {
      subscription_status: status,
      subscription_plan: plan,
      subscription_end: endDate
    });
  }

  // ==========================================
  // CITIES
  // ==========================================

  async getAllCities() {
    try {
      const { data, error } = await supabase
        .from('cities')
        .select('*')
        .order('state_abbr')
        .order('name');

      if (error) throw error;
      return { data, error: null };
    } catch (err) {
      console.error('DealerService getAllCities Error:', err);
      return { data: [], error: err };
    }
  }

  async getActiveCities() {
    try {
      const { data, error } = await supabase
        .from('cities')
        .select('*')
        .eq('is_active', true)
        .order('state_abbr')
        .order('name');

      if (error) throw error;
      return { data, error: null };
    } catch (err) {
      console.error('DealerService getActiveCities Error:', err);
      return { data: [], error: err };
    }
  }

  async createCity(cityData) {
    try {
      // Generate slug
      const slug = cityData.slug ||
        `${cityData.name}-${cityData.state_abbr}`
          .toLowerCase()
          .replace(/[^a-z0-9]+/g, '-');

      const { data, error } = await supabase
        .from('cities')
        .insert([{ ...cityData, slug }])
        .select()
        .single();

      if (error) throw error;
      return { data, error: null };
    } catch (err) {
      console.error('DealerService createCity Error:', err);
      return { data: null, error: err };
    }
  }

  async updateCity(id, updates) {
    try {
      const { data, error } = await supabase
        .from('cities')
        .update(updates)
        .eq('id', id)
        .select()
        .single();

      if (error) throw error;
      return { data, error: null };
    } catch (err) {
      console.error('DealerService updateCity Error:', err);
      return { data: null, error: err };
    }
  }

  async deleteCity(id) {
    try {
      const { error } = await supabase
        .from('cities')
        .delete()
        .eq('id', id);

      if (error) throw error;
      return { error: null };
    } catch (err) {
      console.error('DealerService deleteCity Error:', err);
      return { error: err };
    }
  }

  // ==========================================
  // DEALER CITY ASSIGNMENTS
  // ==========================================

  async getDealerCities(dealerId) {
    try {
      const { data, error } = await supabase
        .from('dealer_cities')
        .select(`
          *,
          city:cities(*)
        `)
        .eq('dealer_id', dealerId);

      if (error) throw error;
      return { data, error: null };
    } catch (err) {
      console.error('DealerService getDealerCities Error:', err);
      return { data: [], error: err };
    }
  }

  async getCityDealers(cityId) {
    try {
      const { data, error } = await supabase
        .from('dealer_cities')
        .select(`
          *,
          dealer:dealers(*)
        `)
        .eq('city_id', cityId)
        .eq('is_active', true);

      if (error) throw error;
      return { data, error: null };
    } catch (err) {
      console.error('DealerService getCityDealers Error:', err);
      return { data: [], error: err };
    }
  }

  async assignCityToDealer(dealerId, cityId, isPrimary = false) {
    try {
      const { data, error } = await supabase
        .from('dealer_cities')
        .insert([{
          dealer_id: dealerId,
          city_id: cityId,
          is_primary: isPrimary,
          is_active: true
        }])
        .select()
        .single();

      if (error) throw error;
      return { data, error: null };
    } catch (err) {
      console.error('DealerService assignCityToDealer Error:', err);
      return { data: null, error: err };
    }
  }

  async removeCityFromDealer(dealerId, cityId) {
    try {
      const { error } = await supabase
        .from('dealer_cities')
        .delete()
        .eq('dealer_id', dealerId)
        .eq('city_id', cityId);

      if (error) throw error;
      return { error: null };
    } catch (err) {
      console.error('DealerService removeCityFromDealer Error:', err);
      return { error: err };
    }
  }

  async updateDealerCityAssignment(id, updates) {
    try {
      const { data, error } = await supabase
        .from('dealer_cities')
        .update(updates)
        .eq('id', id)
        .select()
        .single();

      if (error) throw error;
      return { data, error: null };
    } catch (err) {
      console.error('DealerService updateDealerCityAssignment Error:', err);
      return { data: null, error: err };
    }
  }

  // ==========================================
  // USER PROFILES
  // ==========================================

  async getUserProfile(userId) {
    try {
      const { data, error } = await supabase
        .from('user_profiles')
        .select(`
          *,
          dealer:dealers(*)
        `)
        .eq('id', userId)
        .single();

      if (error) throw error;
      return { data, error: null };
    } catch (err) {
      console.error('DealerService getUserProfile Error:', err);
      return { data: null, error: err };
    }
  }

  async getDealerUsers(dealerId) {
    try {
      const { data, error } = await supabase
        .from('user_profiles')
        .select('*')
        .eq('dealer_id', dealerId)
        .order('created_at');

      if (error) throw error;
      return { data, error: null };
    } catch (err) {
      console.error('DealerService getDealerUsers Error:', err);
      return { data: [], error: err };
    }
  }

  async updateUserProfile(userId, updates) {
    try {
      const { data, error } = await supabase
        .from('user_profiles')
        .update(updates)
        .eq('id', userId)
        .select()
        .single();

      if (error) throw error;
      return { data, error: null };
    } catch (err) {
      console.error('DealerService updateUserProfile Error:', err);
      return { data: null, error: err };
    }
  }

  async assignUserToDealer(userId, dealerId, role = 'dealer_admin') {
    return this.updateUserProfile(userId, {
      dealer_id: dealerId,
      role: role
    });
  }

  // ==========================================
  // DEALER STATS
  // ==========================================

  // ==========================================
  // STRIPE CONNECT
  // ==========================================

  async getDealersWithStripeStatus() {
    try {
      const { data, error } = await supabase
        .from('dealers')
        .select('*, dealer_invitations(id, status, email, invited_at)')
        .order('company_name');
      if (error) throw error;
      return { data, error: null };
    } catch (err) {
      console.error('DealerService getDealersWithStripeStatus Error:', err);
      return { data: [], error: err };
    }
  }

  async updateStripeInfo(dealerId, stripeData) {
    return this.updateDealer(dealerId, {
      stripe_account_id: stripeData.stripe_account_id,
      stripe_connected: stripeData.stripe_connected,
      stripe_connected_at: stripeData.stripe_connected_at,
      stripe_account_type: stripeData.stripe_account_type || 'standard'
    });
  }

  // ==========================================
  // DEALER INVITATIONS
  // ==========================================

  async getInvitationsForDealer(dealerId) {
    try {
      const { data, error } = await supabase
        .from('dealer_invitations')
        .select('*')
        .eq('dealer_id', dealerId)
        .order('invited_at', { ascending: false });
      if (error) throw error;
      return { data, error: null };
    } catch (err) {
      console.error('DealerService getInvitationsForDealer Error:', err);
      return { data: [], error: err };
    }
  }

  async getAllInvitations() {
    try {
      const { data, error } = await supabase
        .from('dealer_invitations')
        .select('*, dealers(company_name, email)')
        .order('invited_at', { ascending: false });
      if (error) throw error;
      return { data, error: null };
    } catch (err) {
      console.error('DealerService getAllInvitations Error:', err);
      return { data: [], error: err };
    }
  }

  // ==========================================
  // DEALER STATS
  // ==========================================

  async getDealerStats(dealerId) {
    try {
      // Get shed count
      const { count: shedCount } = await supabase
        .from('sheds_inventory')
        .select('*', { count: 'exact', head: true })
        .eq('dealer_id', dealerId);

      // Get published shed count
      const { count: publishedCount } = await supabase
        .from('sheds_inventory')
        .select('*', { count: 'exact', head: true })
        .eq('dealer_id', dealerId)
        .eq('is_published', true);

      // Get lead count
      const { count: leadCount } = await supabase
        .from('leads')
        .select('*', { count: 'exact', head: true })
        .eq('dealer_id', dealerId);

      // Get city count
      const { count: cityCount } = await supabase
        .from('dealer_cities')
        .select('*', { count: 'exact', head: true })
        .eq('dealer_id', dealerId)
        .eq('is_active', true);

      return {
        data: {
          totalSheds: shedCount || 0,
          publishedSheds: publishedCount || 0,
          totalLeads: leadCount || 0,
          activeCities: cityCount || 0
        },
        error: null
      };
    } catch (err) {
      console.error('DealerService getDealerStats Error:', err);
      return { data: null, error: err };
    }
  }
}

export const dealerService = new DealerService();
